import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { videoAPI } from '../lib/api';
import { Button } from './shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from './shadcn/card';
import { Badge } from './shadcn/badge';
import { Alert, AlertDescription } from './shadcn/alert';
import { 
  ArrowLeft, 
  Download, 
  Clipboard, 
  Eye, 
  Activity, 
  Clock,
  AlertCircle
} from 'lucide-react';
import { Loader2 } from 'lucide-react';

function Results() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await videoAPI.getVideoResults(videoId);
        setResults(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch results. Please try again.');
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchResults();
    }
  }, [currentUser, videoId]);

  const handleDownload = () => {
    // In a real application, this would download the actual results file
    // For now, we'll just show an alert
    alert('In a real application, this would download your results file in PDF, CSV, or JSON format.');
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Analysis Results</h1>
        </div>
        <div className="flex justify-center items-center h-32">
          <Loader2 className="h-8 w-8 animate-spin mr-2" />
          <span>Processing your video analysis...</span>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Analysis Results</h1>
          <p className="text-gray-600">Detailed insights from your video analysis</p>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
          <Button onClick={handleDownload} className="bg-gradient-to-r from-blue-500 to-blue-600">
            <Download className="mr-2 h-4 w-4" />
            Download Results
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <Clipboard className="h-5 w-5 text-blue-600" />
            </div>
            <CardTitle>Analysis Summary</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p>{results?.summary || 'No summary available.'}</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-full mr-3">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle>Objects Detected</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {results?.objectsDetected?.map((object, index) => (
                <Badge key={index} variant="default">
                  {object}
                </Badge>
              )) || <p className="text-gray-500">No objects detected.</p>}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-full mr-3">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle>Activity Analysis</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p>{results?.activity || 'No activity analysis available.'}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <CardTitle>Analysis Timestamp</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-lg font-medium">
            {results?.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default Results;